import * as axios from "axios"
import { es } from "date-fns/locale"
import { _delete, _get, _patch, _post, _postArchivo, _put } from "./http.js";

/**
 * Producto que factura el consumo de OpenAI. El backend lee este header para
 * decidir la API key: NIA cobra contra OPENAI_API_KEYNIA, y todo lo demás
 * —LIA editorial, NimbusMath— contra OPENAI_API_KEY.
 *
 * Sin header el backend asume LIA, así que sólo hay que marcar explícitamente
 * las llamadas de NIA.
 */
export const PRODUCTO_NIA = 'NIA'

const config = (producto) =>{
    const token = localStorage.getItem("token") || window.sessionStorage.getItem('token')
    return {headers:{
        Authorization: `Bearer ${token}`,
        ...(producto && {'x-nimbus-app': producto})
    }}
}

/**
 * `producto` es un parámetro y no un valor fijo porque esta función tiene dos
 * consumidores que facturan distinto: el chatbot NIA (ChatbotLibro) y
 * NimbusMathIA. Marcar la función entera como NIA cobraría math a la llave
 * equivocada.
 */
export const openAICall = async (body, producto)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/lia-api/llamar-openai`,body,config(producto)).then(response=>{
        return response
    }).catch(err=>{
        return {error:err.response.data}
    })
}

// Sólo lo consume ChatbotLibro (NIA), por eso el producto va fijo. Si algún día
// lo usa también la autoría de LIA, hay que volverlo un parámetro como en openAICall.
export const generateImage = async (prompt) => {
    try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/lia-api/v1/images/generate`, {
            prompt: prompt,
            size: "1024x1024",
            quality: "medium",
            background: "opaque"
        }, config(PRODUCTO_NIA));
        
        if (response.data?.success && response.data?.data?.b64) {
            return `data:image/webp;base64,${response.data.data.b64}`;
        } else {
            console.error('[API] Respuesta inesperada:', response.data);
            throw new Error('Error al generar imagen - respuesta inesperada');
        }
    } catch (error) {
        console.error('[API] Error generando imagen:', error.message);
        if (error.response) {
            console.error('[API] Status:', error.response.status);
            console.error('[API] Error data:', error.response.data);
        }
        return null;
    }
}

// Sólo lo consume ChatbotLibro (NIA), por eso el producto va fijo. Si algún día
// lo usa también la autoría de LIA, hay que volverlo un parámetro como en openAICall.
export const generateAudio = async (text, voice = 'alloy', style = 'educativo') => {
    try {
        console.log('[API] Enviando solicitud TTS:', text.substring(0, 100) + '...');
        console.log('[API] Voice:', voice, 'Style:', style);
        
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/lia-api/generate-audio`, {
            text: text,
            model: 'gpt-4o-mini-tts',
            style: style
        }, config(PRODUCTO_NIA));
        
        console.log('[API] Respuesta TTS status:', response.status);
        console.log('[API] Respuesta TTS data:', response.data);
        
        // Validar la respuesta
        if (!response.data || !response.data.success) {
            console.error('[API] Error: Backend respondió con error:', response.data);
            // Retornar el error específico para manejarlo en el frontend
            return {
                error: true,
                message: response.data?.message || response.data?.error || 'Error desconocido'
            };
        }
        
        // Extraer el audio en base64
        const base64Audio = response.data.data?.audio;
        if (!base64Audio) {
            console.error('[API] Error: No se encontró audio en la respuesta');
            return null;
        }
        
        // Convertir base64 a blob
        const audioBytes = atob(base64Audio);
        const audioArray = new Uint8Array(audioBytes.length);
        for (let i = 0; i < audioBytes.length; i++) {
            audioArray[i] = audioBytes.charCodeAt(i);
        }
        
        const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        console.log('[API] Audio URL creada:', audioUrl);
        console.log('[API] Blob size:', audioBlob.size, 'type:', audioBlob.type);
        return audioUrl;
    } catch (error) {
        console.error('[API] Error generando audio:', error.message);
        if (error.response) {
            console.error('[API] Status:', error.response.status);
            console.error('[API] Error data:', error.response.data);
        }
        return null;
    }
}

// Sólo lo consume ChatbotLibro (NIA), por eso el producto va fijo. Si algún día
// lo usa también la autoría de LIA, hay que volverlo un parámetro como en openAICall.
/**
 * Genera un podcast con múltiples voces usando el endpoint /lia-api/generate-podcast
 * @param {string} script - Guión del podcast con formato "Anfitrión:", "Invitado:", etc.
 * @param {Object} options - Opciones de configuración del podcast
 * @param {string} options.hostVoice - Voz del anfitrión (default: 'alloy')
 * @param {string} options.guestVoice - Voz del invitado (default: 'nova')
 * @param {number} options.hostSpeed - Velocidad del anfitrión 0.5-2.0 (default: 1.0)
 * @param {number} options.guestSpeed - Velocidad del invitado 0.5-2.0 (default: 1.0)
 * @returns {Promise<string|Object>} URL del audio generado o objeto con error
 */
export const generatePodcast = async (script, options = {}) => {
    try {
        const { 
            hostVoice = 'alloy', 
            guestVoice = 'nova',
            hostSpeed = 1.25,
            guestSpeed = 1.25
        } = options;
        
        console.log('[API] Enviando solicitud Podcast:', script.substring(0, 100) + '...');
        console.log('[API] Host:', hostVoice, 'Guest:', guestVoice);
        
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/lia-api/generate-podcast`, {
            script: script,
            hostVoice: hostVoice,
            guestVoice: guestVoice,
            hostSpeed: hostSpeed,
            guestSpeed: guestSpeed
        }, config(PRODUCTO_NIA));
                
        // Validar la respuesta
        if (!response.data || !response.data.success) {
            console.error('[API] Error: Backend respondió con error:', response.data);
            return {
                error: true,
                message: response.data?.message || response.data?.error || 'Error desconocido'
            };
        }
        
        // Extraer el audio en base64
        const base64Audio = response.data.data?.audio;
        if (!base64Audio) {
            console.error('[API] Error: No se encontró audio en la respuesta del podcast');
            return null;
        }
        
        // Convertir base64 a blob
        const audioBytes = atob(base64Audio);
        const audioArray = new Uint8Array(audioBytes.length);
        for (let i = 0; i < audioBytes.length; i++) {
            audioArray[i] = audioBytes.charCodeAt(i);
        }
        
        const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        console.log('[API] Podcast URL creada:', audioUrl);
        console.log('[API] Blob size:', audioBlob.size, 'type:', audioBlob.type);
        return audioUrl;
    } catch (error) {
        console.error('[API] Error generando podcast:', error.message);
        if (error.response) {
            console.error('[API] Status:', error.response.status);
            console.error('[API] Error data:', error.response.data);
        }
        return null;
    }
}

export const getChatbotCacheEntry = async (bookId, lessonId, quickActionId, rol) => {
    const params = new URLSearchParams({ bookId, quickActionId, rol });
    if (lessonId) params.append('lessonId', lessonId);
    return await axios.get(`${process.env.REACT_APP_API_URL}/lia-api/chatbot-cache?${params}`, config())
        .then(response => response.data)
        .catch(() => null);
};

export const saveChatbotCacheEntry = async (body) => {
    return await axios.post(`${process.env.REACT_APP_API_URL}/lia-api/chatbot-cache`, body, config())
        .then(response => response.data)
        .catch(() => null);
};

export const getDocumentoIA = async ({ umaId, rol = 'ALUMNO' }) => {
    if (!umaId) throw new Error('umaId es requerido para obtener Documento IA');
    const rolNormalizado = (rol || 'ALUMNO').toUpperCase();
    const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/lia-api/documento-ia/uma/${umaId}`,
        { ...config(), params: { rol: rolNormalizado } }
    );
    const data = response.data;
    if (!data?.contextoTexto) {
        throw new Error('[DocumentoIA] La respuesta no contiene contextoTexto');
    }
    return data;
};

export const logIn = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/iniciar-sesion`,body).then(response=>{
        return response
    }).catch(err=>{
        console.log(err.response.data)
        return {error:err.response.data}
    })
}

export const solicitarPassword = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/solicitarRestablecimientoContrasenia`,body).then(response=>{
        return response
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const restablecerPassword = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/restablecimientoContrasenia`,body).then(response=>{
        return response
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const cambiarContrasenia = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/cambiarContrasenia`,body,config()).then(response=>{
        return response
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const cambiarContraseniaAlumno = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/cambiarContraseniaAlumno`,body,config()).then(response=>{
        return response
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const cambiarContraseniaPersonal = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/cambiarContraseniaPersonal`,body,config()).then(response=>{
        return response
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const putUsuario = async (usuarioId,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/usuarios/${usuarioId}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const putAlumnoCM = async (correo, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/usuarios/actualizaCargaMasiva/${correo}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const putTutorCM = async (correo, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/usuarios/actualizaTutorCargaMasiva/${correo}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const deleteUsuario = async (usuarioId) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/usuarios/${usuarioId}`,config()).then(response=>{
        return response
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const changeRolUser = async (usuarioId, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/usuarios/cambiarRol/${usuarioId}`,body, config()).then(response=>{
        return response
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const updateCorp = async (escuelaId, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/cambiarPlantel/${escuelaId}`,body, config()).then(response=>{
        return response
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getUserInfo = async ()=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/info`,null,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const verifyEmail = async (body, allInfo = false)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/obtenerPorCorreoElectronico`,body,config()).then(response=>{
        if(allInfo) return response.data;
        return {correoElectronico:response.data.correoElectronico,id:response.data.id, nombres: response.data.nombres, primerApellido: response.data.primerApellido, roles: response.data.roles}
    }).catch(err=>{
        return err
    })
}

export const getUsuarioDetail = async (id)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/usuarios/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const putRecuperarDesactivarUsuario = async (id, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/usuarios/recuperarDesactivarUsuario/${id}`, body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const eliminarUsuario = async (id, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/usuarios/eliminarPermanente/${id}`, body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const buscarEnPlanteles = async (id)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/usuarios/buscarEnPlanteles/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const desactivarUsuario = async (id)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/usuarios/desactivarUsuario/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const eliminarDePlantel = async (id, campusId)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/usuarios/campus/${campusId}/eliminarDelPlantel/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getCarpetas = async (id)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/carpetas/obtenerPorUsuarioCompletas/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getCarpetasPrimarias = async (id, rolActivo, estructuraId, obtenerUMASEliminadas = true)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/carpetas/obtenerCarpetasPrimarias/${id}/${rolActivo}/${estructuraId}/${obtenerUMASEliminadas}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getCarpetasSecundarias = async (id, rolActivo, estructuraId, obtenerUMASEliminadas)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/carpetas/obtenerCarpetasSecundarias/${id}/${rolActivo}/${estructuraId}/${obtenerUMASEliminadas}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getUmasUsuario = async (id)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/carpetas/obtenerUmasUsuario/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getUmasPorCreador = async (id) => {
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/porCreador/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getCarpeta = async (id)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/carpetas/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getDetalleCarpetaPrimaria = async (id)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/carpetas/obtenerInfoCarpetaPrimaria/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getDetalleCarpetaPrimariaLimitada = async (id)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/carpetas/obtenerInfoCarpetaPrimariaLimitada/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getDetalleCarpetaSecundaria = async (id)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/carpetas/obtenerInfoCarpetaSecundaria/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const postCarpeta = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/carpetas`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const putCarpeta = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/carpetas/${id}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const deleteCarpeta = async (id)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/carpetas/${id}`,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const putUMACarpeta = async (idCarpeta, body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/carpetas/putUMACarpeta/${idCarpeta}`, body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const putContenidosCarpeta = async (idCarpeta, body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/carpetas/putContenidosCarpeta/${idCarpeta}`, body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const removeUMACarpeta = async (body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/carpetas/removeUMACarpeta/`, body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const removeContenidosCarpetaMasiva = async (body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/carpetas/removeContenidosCarpeta/`, body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const getUMA = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getDetalleUMA = async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${id}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getAreaConocimiento = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/etiquetas/porDiscriminador/AREA_CONOCIMIENTO`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getTemas = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/etiquetas/porDiscriminador/TEMA`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const crearTag = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/etiquetas`,body,config()).then((response)=>{
        return {data:response.error? response.error : response.data}
    }).catch((err)=>{
        return err
    })
}

export const getUsuarios = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/usuarios`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getNivelesAcademico = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/nivel-academico`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}
export const getNivelAcademico = async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/nivel-academico/${id}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getGradosAcademicosPorLista = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/nivel-academico/obtenerPorLista`,body,config()).then((response)=>{
        return response;
    }).catch((err)=>{
        return err
    })
}

export const getBloques = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/bloques`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getUMADetail = async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${id}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getUMADetailLight = async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/obtenerLight/${id}`,config()).then((response)=>{
        return response
    }).catch((err)=>{
        return err
    })
}

export const postUMA = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

// Libros en los que está armada una lección (solo EDITOR_EDITORIAL en el backend).
export const getLibrosDeUma = async (id) => {
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${id}/libros`, config()).then((response) => {
        return { data: response.data }
    }).catch((err) => {
        return { error: err }
    })
}

export const getUmaEnUso = async (id) => {
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/enUso/${id}`, config()).then((response) => {
        return { response }
    }).catch((err) => {
        console.log(err)
    })
}

export const editUMA = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/${id}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const bloquearUma = async (id, body) => {
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/bloquear/${id}`, body, config()).then((response) => {
        return { response }
    }).catch((err) => {
        console.log(err)
    })
}

export const liberarUma = async (id) => {
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/bloquear/${id}`, { esUsada: false, usuarioEditando: '' }, config()).then((response) => {
        return { response }
    }).catch((err) => {
        console.log(err)
    })
}

export const liberarUmaBeacon = (id) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    fetch(`${process.env.REACT_APP_API_URL}/umas/bloquear/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ esUsada: false, usuarioEditando: '' }),
        keepalive: true,
    });
}

export const editCamposOpcionalesUMA = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/actualizarOpcionales/${id}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const actualizarLiberada = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/actualizarLiberada/${id}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const actualizarLiberadaMasivo = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/actualizarLiberadaMasivo/${id}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const editOrdenBloques = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/actualizarOrdenBloques/${id}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const actualizarAutorUma = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/actualizarAutor/${id}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        return err
    })
}

export const deleteUMA = async (id)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/umas/${id}`,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const eliminarUmasMasivo = async (body, id)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/eliminarMasivo/${id}`, body, config()).then((response)=>{
        return response
    }).catch((err)=>{
        console.log(err)
    })
}

export const updateUMATemp = async (id, body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/desactivaReactivaLeccion/${id}`, body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const updateUMATempMasivo = async (id, body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/desactivaReactivaLeccionMasivo/${id}`, body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getUmasConstructor = async (id, activeRole, esEliminado)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/paraConstructor/${activeRole}/${esEliminado}/${id}`, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getUmasConstructorPapelera = async (id, activeRole)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/paraConstructorPapelera/${activeRole}/${id}`, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getUmasLimitadasConstructor = async (id, activeRole, esEliminado)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/paraConstructorLimitado/${activeRole}/${esEliminado}/${id}`, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getUmasPorArrayId = async (body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/obtenerPorArrayId`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })

}
    
export const obtenerUmasConstructorBusqueda = async (id, activeRole, esEliminado, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/paraConstructorBuscador/${activeRole}/${esEliminado}/${id}`, body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const duplicarUma = async (uma, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/${uma}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const duplicarPlantilla = async (uma, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/duplicarPlantilla/${uma}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getActividadPregunta = async (umaId, bloqueId, actId, pregId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/preguntas`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}


/*  */

/* export const getActividadChecklist = async (umaId, bloqueId, actId, ) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/checklist`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const postChecklistItem = async (umaId, bloqueId, actId, body ) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/checklist`,body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const deleteChecklistItem = async (umaId, bloqueId, actId, itemId ) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/checklist/${itemId}`, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
} */

export const getActividad = async (umaId, bloqueId, actId,actName ) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/${actName}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

/*  */

export const obtenerRespuestas = async (umaId, bloqueId, actName, preguntaId ) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actName}/preguntas/${preguntaId}/respuestas`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const postRespuestas = async (umaId, bloqueId, actName, preguntaId, body ) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actName}/preguntas/${preguntaId}/respuestas`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const updateRespuestas = async (umaId, bloqueId, actName, preguntaId, respuestaId, body  ) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actName}/preguntas/${preguntaId}/respuestas/${respuestaId}`,body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const deleteRespuestas = async (umaId, bloqueId, actName, preguntaId, respuestaId) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actName}/preguntas/${preguntaId}/respuestas/${respuestaId}`, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
} 

export const getFilas = async (umaId, bloqueId, actId, columnaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/columnas/${columnaId}/filas`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const postFilas = async (umaId, bloqueId, actName, columnaId, body ) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actName}/columnas/${columnaId}/filas`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const updateFilas = async (umaId, bloqueId, actId, itemId,filaId, body ) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/columnas/${itemId}/filas/${filaId}`,body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

/* Actividades */

export const postActividadItem = async (umaId, bloqueId, actId, actName, body ) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/${actName}`,body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const updateActividadItem = async (umaId, bloqueId, actId, actName,itemId, body ) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/${actName}/${itemId}`,body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const deleteActividadItem = async (umaId, bloqueId, actId,actName,  itemId ) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actId}/${actName}/${itemId}`, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
} 

//Para bloque checklists

export const postUserCheck = async (idEscuela,idCampus,idGrupo,idMateria,idUsuario, body ) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${idEscuela}/campus/${idCampus}/grupos/${idGrupo}/materia/${idMateria}/usuario/${idUsuario}/checklist`,body, config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getUsersChecks = async (idEscuela,idCampus,idGrupo,idMateria,idUsuario) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${idEscuela}/campus/${idCampus}/grupos/${idGrupo}/materia/${idMateria}/usuario/${idUsuario}/checklist`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

//Actividad Evidencias 

export const getEvidenciaDetalle = async (umaId, bloqueId, actividadId, evidenciaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actividadId}/evidencias/${evidenciaId}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const updateEvidencia = async (umaId, bloqueId, actividadId, evidenciaId, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actividadId}/evidencias/${evidenciaId}`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const postEvidencia = async (umaId, bloqueId, actividadId, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actividadId}/evidencias`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const updateEvidenciaCompleta = async (umaId, bloqueId, actividadId, evidenciaId, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}/actividad/${actividadId}/evidencias/${evidenciaId}/actualizarTodo`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getSeguimientoEvidencia = async (evidenciaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/${evidenciaId}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const darAltaEvidencia = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getListadoEvidencias = async (escuela, campus, grupo, materia) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuela}/campus/${campus}/grupos/${grupo}/materia/${materia}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getEvidenciasAlumno = async (escuela, campus, grupo, materia, alumno) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuela}/campus/${campus}/grupos/${grupo}/materia/${materia}/alumno/${alumno}/seguimientoEvidencias`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const postArchivoEvidencia = async (calificacion, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/${calificacion}/cargaEvidencias`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const deleteArchivoEvidencia = async (calificacion, evidencia)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/${calificacion}/cargaEvidencias/${evidencia}`,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const postComentarioEvidencia = async (calificacion, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/${calificacion}/comentariosAlumno`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const enviarTareaEvidencia = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/enviarTarea`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

//para calificar evidencias

export const getListadoPorEvidencia = async (grupo, materia, libro, uma, evidencia) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/grupo/${grupo}/materia/${materia}/libroContenido/${libro}/umaBloque/${uma}/evidencia/${evidencia}/calificacionEvidencias`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getListadoAlumnos = async (escuela, campus, grupo, materia) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuela}/campus/${campus}/grupos/${grupo}/materia/${materia}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getEvidenciaAlumno = async (escuela, campus, grupo, materia, alumno) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuela}/campus/${campus}/grupos/${grupo}/materia/${materia}/alumno/${alumno}/seguimientoEvidencias`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const calificarRubrica = async (evidencia, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/${evidencia}/calificacionRubrica`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const postComentarioProfesor = async (evidencia, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/${evidencia}/comentariosProfesor`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getDetalleEvidencia = async (materia, libro, agrupador, uma, evidencia, alumno) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/materia/${materia}/libroContenido/${libro}/agrupadorUma/${agrupador}/umaBloque/${uma}/evidencia/${evidencia}/alumno/${alumno}/calificacionEvidencias`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const rechazarTarea = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/rechazarTarea`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}
export const calificarTarea = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/enviarCalificacion`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const bloquearTareaAlumno = async (evidencia, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/${evidencia}/bloquear`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const desbloquearTareaAlumno = async (evidencia, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/${evidencia}/desbloquear`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const bloquearTareaGeneral = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/bloquearParaTodos`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const desbloquearTareaGeneral = async (body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/calificacionEvidencias/desbloquearParaTodos`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getDetalleTareaBloqueada = async (grupo, materia, libro, uma, evidencia) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/grupo/${grupo}/materia/${materia}/libroContenido/${libro}/umaBloque/${uma}/evidencia/${evidencia}/evidenciaBloqueadaGeneral`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

/* fin actividades */

export const postBloque = async (umaId,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const postBloqueConIA = async (umaId,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/crearConIA`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getBloque = async (umaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${umaId}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getBloqueDetalle = async (umaId, bloqueId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/umas/${umaId}/umaBloques/${bloqueId}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const deleteUmaBloque = async (idUma,id)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/umas/${idUma}/umaBloques/${id}`,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const editBloques = async (idUma,body,id) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/umas/${idUma}/umaBloques/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const postArchivos = async (data) =>{
    let token = localStorage.getItem('token') || window.sessionStorage.getItem('token');
    return await axios.post(`${process.env.REACT_APP_API_URL}/archivos/`,data,{headers:{'Content-Type':'multipart/form-data',Authorization:'Bearer '+token}}).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
        return {error: err}
    })
}

export const deleteArchivo = async (url) =>{
    let token = localStorage.getItem('token') || window.sessionStorage.getItem('token');
    return await axios.delete(`${process.env.REACT_APP_API_URL}/archivos/`,{headers:{Authorization:'Bearer '+token},data:{url}}).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
        return {error: err}
    })
}


export const postDescargarImagenExterna = async (url) =>{
    let token = localStorage.getItem('token') || window.sessionStorage.getItem('token');
    return await axios.post(`${process.env.REACT_APP_API_URL}/archivos/descargar-externo`,{url},{headers:{Authorization:'Bearer '+token}}).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
        return {error: err}
    })
}

export const deleteRubricaItem= async (evidenciaId, rubricaId)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/evidencias/${evidenciaId}/rubricas/${rubricaId}`,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const addRubricaItem= async (evidenciaId, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/evidencias/${evidenciaId}/rubricas`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

//Grupos

export const getAgrupadores = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/agrupadores`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const postAgrupadores = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/agrupadores`,body,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const deleteAgrupadores = async (id)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/agrupadores/${id}`,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getDetalleAgrupador= async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/agrupadores/${id}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const editAgrupador = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/agrupadores/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const deleteAgrupador = async (id)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/agrupadores/${id}`,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

//Libros

export const getLibros = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getLibrosGrupoAsignado = async (body) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/obtenerLibrosGrupoAsignado`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const postLibros = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/libros/`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const deleteLibros = async (id, body)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/libros/${id}`,{...config(), data:body}).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getLibrosFavoritos = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/favoritos`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const postLibrosFavoritos = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/libros/favoritos/`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const deleteLibrosFavoritos = async (id)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/libros/favoritos/${id}`,config()).then((response)=>{
        return {response}
    }).catch((err)=>{
        console.log(err)
    })
}

export const getDetalleLibro= async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/${id}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

// Campos propios del libro, sin el árbol de contenidos. Para el formulario "Editar campos".
export const getDetalleLibroConfiguracion = async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/${id}/configuracion`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getLibroVisor= async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/getLibroVisor/${id}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getDetalleLibroContenido= async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/obtenerContenidos/${id}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getDetalleLibroArmado= async (id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/${id}/armado`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}
export const editLibro = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const editLibroInfoPdf = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/actualizarInfoPdf/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const editLibroImagen = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/actualizarImagen/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const editPropietearioLibro = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/actualizarPropietario/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getDetalleNiveles= async (id,idLibro) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/${id}/niveles/${idLibro}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const putPreciosLibro = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/actualizarPrecios/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const putEsNuevoLibro = async (id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/actualizarEsNuevo/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const postNivelLibro = async (idLibro,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/libros/${idLibro}/niveles`,body,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const putNivelLibro = async (idLibro,idNivel,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/${idLibro}/niveles/${idNivel}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getLibroArmado = async (idLibro) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/${idLibro}/armadoWeb`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const postContenido = async (idLibro,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/libros/${idLibro}/contenidos`,body,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const putContenido = async (idLibro,idContenido,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/${idLibro}/contenidos/${idContenido}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const deleteContenido = async (idLibro,idContenido) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/libros/${idLibro}/contenidos/${idContenido}`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const putContenidosLibro = async (idLibro, bodyContenidos) => {
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/${idLibro}/contenidos/actualizarContenidos`,bodyContenidos,config()).then(response=>{
        return response;
    }).catch((err)=>{
        return err
    })
}

export const actualizarAutoresLibros = async () => {
    return await axios.post(`${process.env.REACT_APP_API_URL}/libros/actualizarAutores`, null, config()).then(response=>{
        return response;
    }).catch((err)=>{
        return err
    })
}

export const getActividadesResueltas = async (idEscuela,idCampus,idGrupo,idAlumno,idMateria) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${idEscuela}/campus/${idCampus}/grupos/${idGrupo}/alumno/${idAlumno}/calificaciones/materia/${idMateria}/actividadesResueltas`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getActividadesResueltasLibro = async (idGrupo,idLibro) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/grupo/${idGrupo}/calificaciones/libro/${idLibro}/actividadesResueltasLibro`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getActividadesResueltasLibros = async (idGrupo,idLibros) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/grupo/${idGrupo}/calificaciones/libros/actividadesResueltasLibros`,idLibros,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getUmasLibro = async (idLibro) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/umas/${idLibro}`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getActividadesResueltasAlumno = async (idAlumno, idGrupo, idLibro) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/grupo/${idGrupo}/calificaciones/libro/${idLibro}/actividadesResueltasAlumno/${idAlumno}`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getPromedioAlumnoLibro = async (idAlumno, idGrupo, idLibro) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/grupo/${idGrupo}/calificaciones/libro/${idLibro}/getPromedioAlumnoLibro/${idAlumno}`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getPromedioGrupoLibro = async (idGrupo, idLibro) => {
    return await axios.get(`${process.env.REACT_APP_API_URL}/grupo/${idGrupo}/calificaciones/libro/${idLibro}/getPromedioGrupoLibro`, config()).then(response => {
        return { data: response.data }
    }).catch((err) => {
        return err
    })
}

export const getActividadesResueltasGrupo = async (idGrupo, idLibro) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/grupo/${idGrupo}/calificaciones/libro/${idLibro}/actividadesResueltasGrupoCompleto`,config()).then(response=>{
        return {data:response.data[0]}
    }).catch((err)=>{
        return err
    })
}

export const getEvaluacionesResueltasLibro = async (idGrupo,idLibro) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/grupo/${idGrupo}/materia/${idLibro}`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getEvaluacionesResueltasAlumno = async (idAlumno, idGrupo, idLibro, idUma) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/grupo/${idGrupo}/alumno/${idAlumno}/materia/${idLibro}/evaluacion/${idUma}`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getEvaluacionesResueltasAlumnos = async (body, idGrupo) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/calificacionEvaluaciones/grupo/${idGrupo}/materias/evaluaciones/alumnos`, body,config()).then(response=>{
        return response.data;
    }).catch((err)=>{
        return err
    })
}

export const getInsigniasPorLibro = async (idEscuela,idCampus,idGrupo,idAlumno,idMateria)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${idEscuela}/campus/${idCampus}/grupos/${idGrupo}/alumno/${idAlumno}/calificaciones/materia/${idMateria}/insigniasMateria`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getInsigniasPorMateria = async (idMateria, idAlumno)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/calificaciones/alumno/${idAlumno}/materia/${idMateria}/insigniasMateria`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const postCalificacion = async (idEscuela,idCampus,idGrupo,idAlumno,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${idEscuela}/campus/${idCampus}/grupos/${idGrupo}/alumno/${idAlumno}/calificaciones`,body,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const postEvaluacion = async (idEscuela,idCampus,idGrupo,idAlumno,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${idEscuela}/campus/${idCampus}/grupos/${idGrupo}/alumno/${idAlumno}/evaluaciones`,body,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })

}
export const liberarlibro = async (idLibro,body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/libros/liberar/${idLibro}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const librosLiberados = async ()=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/libros/liberados`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}


// Endpoints Escolar

export const getEscuela = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/editorial-distribucion`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getEscuelaDetail = async (escuelaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/editorial-distribucion`,config()).then(response=>{
        return {data:response.data}
    }).catch((error)=>{
        return error
    })
}

export const getEscuelaPlantelesLicencia = async (escuelaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/plantelesLicencias`,config()).then(response=>{
        return {data:response.data}
    }).catch((error)=>{
        return error
    })
}

export const getLibrosPlantel = async (campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/campus/${campusId}`,config()).then(response=>{
        return {data:response.data}
    }).catch((error)=>{
        return error
    })
}

export const getAlumnosGruposLibrosPlantel = async (campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/campus/alumnosGruposLibros/${campusId}`,config()).then(response=>{
        return {data:response.data}
    }).catch((error)=>{
        return error
    })
}

export const getEscuelaPaquetes = async (escuelaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/editorial-distribucion/porPaquetes`,config()).then(response=>{
        return {data:response.data}
    }).catch((error)=>{
        return error
    })
}

export const getEscuelaDetailOptimizado = async (escuelaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/editorial-distribucion/otraVersion`,config()).then(response=>{
        return {data:response.data}
    }).catch((error)=>{
        return error
    })
}


export const getDirectoresCorporativos = async (escuelaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/editorial-distribucion/directores`,config()).then(response=>{
        return {data:response.data}
    }).catch((error)=>{
        return error
    })
}

export const getLibrosCampusCorporativo = async (corporativoId, campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${corporativoId}/libros/${campusId}`,config()).then(response=>{
        return {data:response.data}
    }).catch((error)=>{
        return error
    })
}

export const getLibrosCorporativo = async (corporativoId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${corporativoId}/librosCorp`,config()).then(response=>{
        return {data:response.data}
    }).catch((error)=>{
        return error
    })
}

export const postEscuela = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas`,body,config()).then(response=>{
        return response
    }).catch((error)=>{
        return error
    })
}

export const putEscuela = async (escuelaId, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}`,body,config()).then(response=>{
        return response
    }).catch((error)=>{
        return error
    })
}

export const deleteEscuela = async (escuelaId, body) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}`,{...config(), data:body}).then(response=>{
        if(response.error){
            throw response.error;
        }
        return {data:response.data}
    }).catch((error)=>{
        console.log(error)
        return error
    })
}

export const getCampus = async (escuelaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getCampusDetail = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getCampusDetailLight = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/menosInfo`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getUsuariosPlantel = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/obtenerUsuarios`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getUbicacionUsuariosPlantel = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/obtenerUsuariosUbicacion`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getUbicacionTodos = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/obtenerUbicacionTodos`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getUbicacionTutoresPlantel = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/obtenerTutoresUbicacion`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getCampusDetailParaGrupo = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/obtenerParaEditar/${campusId}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getGruposPlantel = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/obtenerSoloGrupos`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getGruposPlantelSinDatos = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/obtenerNombreGrupos`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getGruposAsignaturaPlantel = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/obtenerGruposConAsignaturas`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getLibrosProfesor = async (escuelaId,campusId,profesorId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/profesores/usuario/${profesorId}/obtenerLibros`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const getGruposProfesor = async (escuelaId,campusId,profesorId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/profesores/usuario/${profesorId}/obtenerGruposPorId`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const deletePlantel = async (escuelaId,campusId) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}`,config()).then((response)=>{
        return response
    }).catch((err)=>{
        return err
    })
}

export const deletePlantelCompleto = async (escuelaId,campusId) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/eliminarCompleto`,config()).then((response)=>{
        return response
    }).catch((err)=>{
        return err
    })
}

export const getPaises = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/paises`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getInteractivos = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/interactivos`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getPracticarios = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/interactivos/practicarios`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getPaisDetalle = async (paisId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/paises/${paisId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getEstados = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/estados`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getEstadoDetalle = async (estadoId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/estados/${estadoId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postCampus = async (escuelaId,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const editCampus = async (escuelaId,campusId,body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getNivelesCampus = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getNivelesConGrados = async (escuelaId, campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos/obtenerNivelesConGrados/${campusId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getNivelesConGradosMaM = async (escuelaId, campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos/obtenerNivelesConGradosMaM/${campusId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postNivelCampus = async (escuelaId,campusId,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const editNivelCampus = async (escuelaId,campusId,nivelId,body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos/${nivelId}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const deleteNivelCampus = async (escuelaId,campusId,nivelId)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos/${nivelId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getGradosCampus = async (escuelaId,campusId,nivelId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos/${nivelId}/grados-academicos`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {err:err.response.data}
    })
}

export const postGradoCampus = async (escuelaId,campusId,nivelId,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos/${nivelId}/grados-academicos`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const editGradoCampus = async (escuelaId,campusId,nivelId,gradoId,body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos/${nivelId}/grados-academicos/${gradoId}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const deleteGradoCampus = async (escuelaId,campusId,nivelId,gradoId)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/niveles-academicos/${nivelId}/grados-academicos/${gradoId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getCalendario = async (escuelaId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/calendario`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postCalendario = async (escuelaId,campusId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/calendario`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const editCalendario = async (escuelaId,campusId,calendarioId,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/calendario/${calendarioId}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const deleteCalendario = async (escuelaId,campusId,calendarioId)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/calendario/${calendarioId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getProfesores = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/profesores`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getGruposCampus = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getGruposCampusFiltrados = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/filtrados`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getGruposCampusFiltradosConAlumnos = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/alumnos/filtrados`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postGrupo = async (escuelaId,campusId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const putGrupo = async (escuelaId,campusId,grupoId,body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const deleteGrupo = async (escuelaId,campusId,grupoId)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}`,config()).then(response=>{
        return response
    }).catch(err=>{
        return err
    })
}

export const deleteContenidoGrupo = async (escuelaId,campusId,grupoId, boolEliminar)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/eliminaContenidoGrupo/${boolEliminar}`,config()).then(response=>{
        return response
    }).catch(err=>{
        return err
    })
}

export const getAlumnosGrupo = async (escuelaId,campusId,grupoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/alumnos`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postAlumnoGrupo = async (escuelaId,campusId,grupoId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/alumnos`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const deleteAlumnoGrupo = async (escuelaId,campusId,grupoId,alumnoId)=>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/alumnos/${alumnoId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const deleteAlumnosGrupo = async (escuelaId, campusId, grupoId, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/alumnos/eliminarAlumnosGrupo`,body, config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const agregarAlumnosGrupo = async (escuelaId, campusId, grupoId, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/alumnos/agregarAlumnosGrupo`,body, config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getMateriasGrupo = async (escuelaId,campusId,grupoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/materias/obtenerMaterias`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getMateriasGrupoInfoReducida = async (escuelaId,campusId,grupoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/materias/obtenerMateriasInfoReducida`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}
export const getDetalleMateriaGrupo = async (escuelaId,campusId,grupoId,materiaId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/materias/${materiaId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}
export const getEvaluacion = async (escuelaId,campusId,grupoId,materiaId,libroContenidoId,alumnoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/materia/${materiaId}/libroContenido/${libroContenidoId}/alumno/${alumnoId}/evaluacion`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postMateriaGrupo = async (escuelaId,campusId,grupoId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/materias`,body,config()).then(response=>{
        return response.data
    }).catch(err=>{
        return err
    })
}

export const deleteMateriaGrupo = async (escuelaId,campusId,grupoId,materiaId) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/materias/${materiaId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const deleteMateriasDeGrupo = async (escuelaId, campusId, grupoId, body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/materias/eliminarMateriasGrupo`, body, config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getAsignaturasGrupo = async (escuelaId,campusId,grupoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/asignaturas`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getAsignatura = async (asignaturaId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/asignaturas/${asignaturaId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

// Igual que getAsignatura pero solo con los campos que usan los reportes del profesor (~9 veces menos datos).
export const getAsignaturaReportes = async (asignaturaId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/asignaturas/${asignaturaId}/reportes`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postAsignaturaGrupo = async (escuelaId,campusId,grupoId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/asignaturas`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const putAsignaturaGrupo = async (escuelaId, campusId, grupoId,asignaturaId, body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/asignaturas/${asignaturaId}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const deleteAsignaturaGrupo = async (escuelaId,campusId,grupoId,asignaturaId) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/asignaturas/${asignaturaId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const eliminarAsignaturasProfesoresGrupo = async (escuelaId,campusId,grupoId,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/eliminarAsignaturaProfesor`,body, config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const eliminarAsignaturas = async (escuelaId,campusId,grupoId,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}/asignaturas/eliminarAsignaturas`,body, config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getGrupo = async (escuelaId,campusId,grupoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/grupos/${grupoId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}


export const getAlumnos = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getAlumnosOptimizado = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/menorInfo`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getAlumnosOptimizadoAlumnos = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/menorInfoAlumnos`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getAlumnosFamilias = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/alumnosFamilias`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getTutoresFamilias = async (escuelaId, campusId, userId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/tutores/${userId}/tutoresFamilias`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const obtenerPlantelesAlumnoYEliminar = async (escuelaId, campusId, alumnoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/${alumnoId}/obtenerPlantelesYEliminar`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const obtenerPlantelesAlumnos = async (escuelaId, campusId, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/obtenerPlantelesTodosAlumnos`, body , config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const promoverAlumnosMasiva = async (escuelaId, campusId, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/promoverAlumnos`, body , config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const promoverAlumnosPlantel = async (escuelaId, campusId, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/promoverTodosAlumnos`, body, config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getAlumnosInactivos = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/papelera/alumno`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getTutoresInactivos = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/tutores/papelera/tutor`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const obtenerPlantelesTutores = async (escuelaId,campusId, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/tutores/obtenerPlantelesTodosTutores`, body , config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const obtenerPlantelesTutorYEliminar = async (escuelaId,campusId, tutorId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/tutores/${tutorId}/obtenerPlantelesYEliminar`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const obtenerPlantelesUsuarios = async (body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/obtenerPlantelesTodosUsuarios`, body , config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const obtenerPlantelesUsuarioYEliminar = async (usuarioId, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/${usuarioId}/obtenerPlantelesYEliminar`, body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getAlumnoDetalle = async (escuelaId,campusId,alumnoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/${alumnoId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getAlumnoInfo = async (escuelaId,campusId,alumnoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/${alumnoId}/info`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postAlumno = async (escuelaId,campusId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos`,body,config()).then(response=>{
        return {_id:response.data._id}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const putAlumno = async (escuelaId,campusId,alumnoId,body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/${alumnoId}`,body,config()).then(response=>{
        return {_id:response.data._id}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const deleteAlumno = async (escuelaId,campusId,alumnoId) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/alumnos/${alumnoId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getTutorDetalle = async (escuelaId,campusId,tutorId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/tutores/${tutorId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getFamiliaTutorId = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/familias/tutores/user`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getTutores = async (escuelaId,campusId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/tutores`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postTutor = async (escuelaId,campusId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/tutores`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const deleteTutor = async (escuelaId, campusId, tutorId) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/tutores/${tutorId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getUsuarioRegistrado = async (escuelaId, campusId, padre, body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/${!padre? 'profesores' : 'tutores'}/buscarUsuario`,body, config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const postFamilia = async (escuelaId,campusId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/familias`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const putFamilia = async (escuelaId,campusId,familiaId,body)=>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/familias/${familiaId}`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getFamiliaAlumno = async (escuelaId,campusId,alumnoId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/familias/alumno/${alumnoId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getFamiliaTutor = async (escuelaId,campusId,tutorId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/familias/tutor/${tutorId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getFamiliaDetalle = async (escuelaId,campusId,familiaId)=>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/campus/${campusId}/familias/${familiaId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postUsuarioCampus = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios`,body,config()).then(response=>{
        return response
    }).catch(err=>{
        return err
    })
}

export const getFacturasUsuario = async (usuarioId) => {
    return await axios.get(`${process.env.REACT_APP_API_URL}/market-place/facturacion/usuario/${usuarioId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getMisCompras = async (usuarioId) => {
    return await axios.get(`${process.env.REACT_APP_API_URL}/market-place/compras/obtenerPorUsuario/${usuarioId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postFacturacion = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/market-place/facturacion`,body,config()).then(response=>{
        return response
    }).catch(err=>{
        return err
    })
}

export const getInfoFacturacionUsuario = async (usuarioId) => {
    return await axios.get(`${process.env.REACT_APP_API_URL}/market-place/datosFacturacion/usuario/${usuarioId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postInfoFacturacionUsuario = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/market-place/datosFacturacion`,body,config()).then(response=>{
        return response
    }).catch(err=>{
        return err
    })
}

export const putInfoFacturacionUsuario = async (body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/market-place/datosFacturacion`,body,config()).then(response=>{
        return response
    }).catch(err=>{
        return err
    })
}
export const deleteInfoFacturacionUsuario = async (usuarioId) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/market-place/datosFacturacion/${usuarioId}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

// Endpoints Marketplace
//-----------------------------------------------------Cursos-------------------------------------------------------------
export const postCourse = async (cursosId,campusId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${cursosId}/campus/${campusId}/cursos`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const postCourseCategory = async (cursosId,body)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${cursosId}/cursosCategorias`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getCursos = async (cursosId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${cursosId}/campus/${campusId}/cursos`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
    
}

export const getAllCursos = async (cursosId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${cursosId}/cursosCategorias`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
    
}

export const getSpecificCategoria = async (escuelaId,categoriaId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${escuelaId}/cursosCategorias/${categoriaId}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
    
}

export const getSpecificCourse = async (cursosId,campusId,id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${cursosId}/campus/${campusId}/cursos/${id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}
export const deleteCourse = async (cursosId,campusId,Id) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${cursosId}/campus/${campusId}/cursos/${Id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}
export const getSpecicCategories = async (cursosId,Id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${cursosId}/cursosCategorias/${Id}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
    
}
//-----------------------------------------------------Academias-----------------------------------------------------------
export const getAcademias = async (academiasId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/rutaAprendizaje`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
    
}

export const getSpecificAcademic= async (academiasId,campusId,Id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/rutaAprendizaje/${Id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}


export const postAcademias = async (academiasId,campusId,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/rutaAprendizaje`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const putAcademias = async (academiasId,campusId,id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/rutaAprendizaje/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}


export const deleteAcademic = async (academiasId,campusId,Id) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/rutaAprendizaje/${Id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const CreateAcademicCategories = async (IdAcademic,idCampus,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${IdAcademic}/campus/${idCampus}/rutaAprendizajeCategorias`,body,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getAllAcademias = async (academiasId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/rutaAprendizajeCategorias`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
    
}

//------------------------------------------Items-----------------------------------------------------------
export const getItems = async (academiasId,campusId) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/items`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
    
}

export const getSpecificItem= async (academiasId,campusId,Id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/items/${Id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}


export const postItem = async (academiasId,campusId,body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/items`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const putItem = async (academiasId,campusId,id,body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/items/${id}`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}


export const deleteItem = async (academiasId,campusId,Id) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/escuelas/${academiasId}/campus/${campusId}/items/${Id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}



//----------------------------------------Maestros-------------------------------------------------------------
export const getAllTeachers = async (profesorEscuela,profesorCampus) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/${profesorEscuela}/campus/${profesorCampus}/profesores`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
    
}
export const getSpecicTeachers = async (Id) =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/escuelas/628d7d3a2cb39c000ba8b8f5/campus/628d7f022cb39c000ba8b933/profesores/${Id}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
    
}
//-------------------------------------ENDPOINTS de metodo de pagos--------------------------------------------------
export const postPasarela = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/market-place/secret`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}
export const postCheckout = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/market-place/checkout`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}
//-------------------------------------ENDPOINTS Carro de compras///--------------------------------------------------
export const getShopCar = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/carritoCompras`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const postShopCar = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/carritoCompras`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const putShopItem = async (body) =>{
    return await axios.put(`${process.env.REACT_APP_API_URL}/carritoCompras/cambiarProducto`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

export const deleteShopItem = async (user,item) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/carritoCompras/user/${user}/item/${item}`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}

//-------------------------------------------Comprados----------------------------------------------------------------

export const getProductosAlumnos = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/productosComprados/misProductos`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}


//-------------------------------------------Favoritos----------------------------------------------------------------
export const createfavorite = async (body) =>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/market-place/favoritos`,body,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}
export const getAllFavorites = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/market-place/favoritos`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log("paso aslgo",err)
    })
}
export const getAllProducts = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/market-place/compras`,config()).then((response)=>{
        return {data:response.data}
    }).catch((err)=>{
        console.log(err)
    })
}
export const deleteFavorite = async (Id) =>{
    return await axios.delete(`${process.env.REACT_APP_API_URL}/market-place/favoritos/${Id}`,config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return err
    })
}

export const getInfo=async()=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/info`,{},config()).then(response=>{
        return {data:response.data}
    }).catch(err=>{
        return {error:err.response.data}
    })
}

export const getTodosChecklist = async () =>{
    return await axios.get(`${process.env.REACT_APP_API_URL}/checklist/obtenerTodosChecklist`,config()).then(response=>{
        return {data:response.data}
    }).catch((err)=>{
        return err
    })
}

export const obtenerUsuarioPorEstructura = async (body, allInfo = false)=>{
    return await axios.post(`${process.env.REACT_APP_API_URL}/usuarios/obtenerPorCorreoElectronicoYEstructura`,body,config()).then(response=>{
        if(allInfo) return response.data;
        return {correoElectronico:response.data.correoElectronico,id:response.data.id, 
            nombres: response.data.nombres, primerApellido: response.data.primerApellido, 
            roles: response.data.roles, ubicacion: response.data.ubicacion}
    }).catch(err=>{
        return err
    })
}

export const getLicencia = (rutaId) => _get(`/licencias/${rutaId}`);
export const getLicencias = () => _get('/licencias');
export const getLicenciasEditorial = (editorialId) => _get(`/licencias/porEditorial/${editorialId}`);
export const postLicencia = (body) => _post(`/licencias`,body);
export const putLicencia = (licenciaId, body) => _put(`/licencias/${licenciaId}`, body);
export const changeStatusLicencia = (licenciaId, body) => _put(`/licencias/actualizarStatus/${licenciaId}`, body);
export const deleteLicencia = (licenciaId) => _delete(`/licencias/${licenciaId}`);
export const changePreciosLicencia = (licenciaId, body) => _put(`/licencias/actualizarPrecios/${licenciaId}`, body);

export const changeStatusPlantel = (plantelId, body) => _put(`/plantel/actualizarStatus/${plantelId}`, body);
export const changeStatusCorporativo = (corporativoId, body) => _put(`/corporativos/actualizarStatus/${corporativoId}`, body);
export const getPlantelesConLicencia = (licenciaId, licenciaReemplazo) => _get(`/licencias/obtenerPorLicencia/${licenciaId}/${licenciaReemplazo}`);
export const getLicenciasCorporativoEditorial = (corporativoId) => _get(`/licencias/obtenerPorCorporativo/${corporativoId}`);

export const getMateria = (materiaId) => _get(`/etiquetas/${materiaId}`);
export const changeStatusMateria = (materiaId, body) => _put(`/etiquetas/actualizarStatus/${materiaId}`, body);
export const putMateria = (materiaId, body) => _put(`/etiquetas/${materiaId}`, body);
export const deleteMateria = (materiaId) => _delete(`/etiquetas/${materiaId}`);
export const getMateriasEditorial = (editorialId) => _get(`/etiquetas/porEditorial/${editorialId}`);
export const getMateriasEditorialActivo = (editorialId) => _get(`/etiquetas/porEditorialActivo/${editorialId}`);
export const getMateriasPlantel = (plantelId) => _get(`/etiquetas/porPlantel/${plantelId}`);
export const getMateriasEditorialPlantel = (plantelId) => _get(`/etiquetas/porEditorialPlantel/${plantelId}`);
export const getMateriasEditorialPlantelLight = (plantelId) => _get(`/etiquetas/porEditorialPlantelLight/${plantelId}`);
export const getMateriasCategoria = (categoriaId) => _get(`/etiquetas/porCategoria/${categoriaId}`);
export const updateMateriasCategoria = (categoriaId, body) => _put(`/etiquetas/updtMateriasCategoria/${categoriaId}`, body);

export const postCategoria = (body) => _post(`/categoriaMaterias`,body);
export const getCategoria = (categoriaId) => _get(`/categoriaMaterias/${categoriaId}`);
export const changeStatusCategoria = (categoriaId, body) => _put(`/categoriaMaterias/actualizarStatus/${categoriaId}`, body);
export const putCategoria = (categoriaId, body) => _put(`/categoriaMaterias/${categoriaId}`, body);
export const deleteCategoria = (categoriaId) => _delete(`/categoriaMaterias/${categoriaId}`);
export const getCategoriasPlantel = (plantelId) => _get(`/categoriaMaterias/porPlantel/${plantelId}`);
export const getCategoriasEditorial = (editorialId) => _get(`/categoriaMaterias/porEditorial/${editorialId}`);
export const getCategoriasEditorialActivo = (editorialId) => _get(`/categoriaMaterias/porEditorialActivo/${editorialId}`);
export const getCategoriasEditorialPlantel = (plantelId) => _get(`/categoriaMaterias/porEditorialPlantel/${plantelId}`);

export const getUmasPorMaterias = (body) => _post('/umas/obtenerPorMaterias', body);

export const getProfesoresMaterias = (campusId) => _get(`/campus/${campusId}/obtenerProfesoresMaterias`);
export const obtenerActividadesRealizadasPorGrupo = (grupoId, libroId) => _get(`/grupo/${grupoId}/calificaciones/libro/${libroId}/actividadesResueltasGrupoCompleto`);
export const actualizarActividadesEvaluablesUmasEditorial = (editorialId) => _get(`/editoriales/${editorialId}/actualizarUmas`);
export const actualizarActividadesEvaluablesUmasCampus = (campusId) => _get(`/plantel/${campusId}/actualizarUmas`);
export const actualizarActividadesEvaluablesTodasUmas = (userId) => _get(`/umas/${userId}/actualizarAEUmas`);
export const generarZipsUmaBloques = (userId) => _get(`/umas/${userId}/generarZipsUmaBloques`);
export const generarZipsLibros = (librosIds) => _post(`/libros/generarZipsMasivo`, { libros: librosIds });
export const postLibroPiloto = (body) => _post(`/libroPiloto`,body);
export const getAutoresIndependientes = (editorialId) => _get(`/editoriales/${editorialId}/obtenerAutoresIndependientes`);
export const getLibrosAutorIndependiente = (editorialId, idAutorIndependiente) => _get(`/editoriales/${editorialId}/obtenerAutorIndependiente/${idAutorIndependiente}`);
export const getUsuariosEditorial = (editorialId) => _get(`/editoriales/${editorialId}/obtenerUsuarios`);

//Obtener libros liberados por estructura por rol
export const getLibrosLiberadosEditorial = (editorialId, rol) => _get(`/editoriales/${editorialId}/obtenerLibrosLiberados/${rol}`);
export const getLibrosLiberadosEditorialLight = (editorialId, rol) => _get(`/editoriales/${editorialId}/obtenerLibrosLiberadosLight/${rol}`);
export const getLibrosPortadasEditorial = (editorialId, rol) => _get(`/editoriales/${editorialId}/obtenerLibrosPortadas/${rol}`);
export const getLibrosLiberadosDistribucion = (distribucionId) => _get(`/distribuciones/${distribucionId}/obtenerLibrosLiberados`);
export const getLibrosLiberadosCorporativo = (corporativoId) => _get(`/escuelas/${corporativoId}/obtenerLibrosLiberados`);
export const getLibrosLiberadosCampus = (campusId, rol) => _get(`/campus/${campusId}/obtenerLibrosLiberados/${rol}`);
export const getLibrosLiberadosLightCampus = (campusId, rol) => _get(`/campus/${campusId}/obtenerLibrosLiberadosLight/${rol}`);
export const getUsuariosPapeleraEditorial = (editorialId) => _get(`/editoriales/${editorialId}/obtenerUsuariosPapelera`);
export const changePreciosHibridos = (editorialId, body) => _put(`/editoriales/${editorialId}/actualizaPrecioHibrido`, body);

//Obtener proyectos
export const getProyectoPorId = (id) => _get(`/proyectos/${id}`);
export const postProyecto = (body) => _post(`/proyectos`,body);
export const putProyecto = (id, body) => _put(`/proyectos/${id}`,body);
export const getProyectosEditorial = (id, rol) => _get(`/proyectos/porEditorial/${rol}/${id}`);
export const getProyectosPlantel = (id) => _get(`/proyectos/porPlantel/${id}`);
export const verificarIdentificadorProyectos = (identificador) => _get(`/proyectos/verificarIdentificador/${identificador}`);
export const deleteProyecto = (id) => _delete(`/proyectos/${id}`);
export const getPreciosEditorial = (id) => _get(`/libros/listaPrecios/${id}`);


//Paquetes cobranza
export const postPaqueteCobranza = (body) => _post("/paquetesCobranza", body);
export const putPaqueteCobranza = (paqueteId, body) => _put(`/paquetesCobranza/${paqueteId}`, body);
export const putPrecioVentaPaqueteCobranza = (paqueteId, body) => _put(`/paquetesCobranza/${paqueteId}/actualizarPrecioVenta`, body);
export const deletePaqueteCobranza = (paqueteId) => _delete(`/paquetesCobranza/${paqueteId}`);
export const getPaquetesCobranzaCreador = (id) => _get(`/paquetesCobranza/porCreador/${id}`);
export const getPaquetesCobranzaEditorial = (id) => _get(`/paquetesCobranza/porEditorial/${id}`);
export const getPaquetesCobranzaDistribuidor = (id) => _get(`/paquetesCobranza/porDistribucion/${id}`);
export const getPrecioLibrosHibridos = (id) => _get(`/editoriales/precioHibrido/${id}`);

//Ecosistemas
export const getEcosistemasEstructura = (tipoEstructuraRequest, estructuraId) => _get(`/ecosistema/${tipoEstructuraRequest}/${estructuraId}`);
export const getEcosistemasEstructuraLight = (tipoEstructuraRequest, estructuraId) => _get(`/ecosistema/${tipoEstructuraRequest}/${estructuraId}/light`);
export const postEcosistema = (body) => _post("/ecosistema", body);
export const putEcosistema = (ecosistemaId, body) => _put(`/ecosistema/${ecosistemaId}`, body);
export const putPreciosEcosistema = (ecosistemaId, body) => _put(`/ecosistema/${ecosistemaId}/precios`, body);
export const deleteEcosistema = (ecosistemaId) => _delete(`/ecosistema/${ecosistemaId}`);
export const getEcosistema = (ecosistemaId) => _get(`/ecosistema/${ecosistemaId}`);

//Categorias de libros
export const getCategoriasLibrosEstructura = (tipoEstructuraRequest, estructuraId) => _get(`/categoriasLibro/${tipoEstructuraRequest}/${estructuraId}`);
export const getCategoriasLibrosEstructuraLight = (tipoEstructuraRequest, estructuraId) => _get(`/categoriasLibro/${tipoEstructuraRequest}/${estructuraId}/light`);
export const postCategoriasLibros = (body) => _post("/categoriasLibro", body);
export const putCategoriasLibros = (categoriasLibroId, body) => _put(`/categoriasLibro/${categoriasLibroId}`, body);
export const deleteCategoriasLibros = (categoriasLibroId) => _delete(`/categoriasLibro/${categoriasLibroId}`);

//Reportes de lección
export const postLessonReport = (body) => _post("/lesson-reports", body);
export const getLessonReportsByUser = (userId) => _get(`/lesson-reports/usuario/${userId}`);
export const getLessonReportById = (id) => _get(`/lesson-reports/${id}`);
export const assignLessonReport = (id, assignedTo) => _patch(`/lesson-reports/${id}/assign`, { assignedTo });
export const resolveLessonReport = (id) => _patch(`/lesson-reports/${id}/resolve`);
export const replyLessonReport = (id, body) =>
  body instanceof FormData
    ? _postArchivo(`/lesson-reports/${id}/reply`, body)
    : _post(`/lesson-reports/${id}/reply`, body);
export const reviewLessonReport = (id) => _patch(`/lesson-reports/${id}/review`);
export const reopenLessonReport = (id) => _patch(`/lesson-reports/${id}/reopen`);
export const cancelLessonReport = (id) => _patch(`/lesson-reports/${id}/cancel`);
export const deleteLessonReport = (id) => _delete(`/lesson-reports/${id}`);
export const getCategoriasLibros = (categoriasLibroId) => _get(`/categoriasLibro/${categoriasLibroId}`);